/**
 * RecentSearchesDropdown — search-UX §4.4.
 *
 * Renders below the SearchBar when the input has focus AND query
 * is empty.  Up to 8 persisted recent searches with per-row clear
 * + a global "Clear all" affordance.  Inspired by Google Photos'
 * empty-state search panel, YouTube mobile search, Pinterest's
 * recent-searches strip — focus state of an empty input is prime
 * real estate for discoverability.
 *
 * Tap a recent → fires the `onPick` callback (parent injects it
 * into the SearchBar's local query state, the existing 300ms
 * debounce then commits to Redux).
 */

import React from 'react';
import {
  FlatList,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useAppDispatch, useAppSelector } from '../../../store/store';
import { fpColors } from '../../../styles/theme';
import {
  clearRecents,
  removeRecent,
  selectRecentSearches,
} from '../recentSearchesSlice';

interface RecentSearchesDropdownProps {
  /** Called when the user taps a recent.  Parent applies the query
   *  to the SearchBar's local state. */
  onPick: (query: string) => void;
}

export const RecentSearchesDropdown: React.FC<RecentSearchesDropdownProps> = ({
  onPick,
}) => {
  const dispatch = useAppDispatch();
  const recents = useAppSelector(selectRecentSearches);

  if (recents.length === 0) return null;

  const handleClearAll = (): void => {
    // CLAUDE.md — Alert.alert is a no-op on web; use window.confirm.
    if (Platform.OS === 'web') {
      if (
        typeof window !== 'undefined' &&
        window.confirm('Clear all recent searches?')
      ) {
        dispatch(clearRecents());
      }
    } else {
      // On native, fire-and-forget.  No confirm sheet — recents are
      // ephemeral enough.  Revisit if user complaints arrive.
      dispatch(clearRecents());
    }
  };

  return (
    <View style={styles.dropdown} accessibilityLabel="Recent searches">
      <View style={styles.headerRow}>
        <Text style={styles.heading}>Recent</Text>
        <Pressable
          onPress={handleClearAll}
          hitSlop={6}
          accessibilityRole="button"
          accessibilityLabel="Clear all recent searches"
        >
          <Text style={styles.clearAll}>Clear</Text>
        </Pressable>
      </View>
      <FlatList
        data={recents}
        keyExtractor={(item) => `recent-${item}`}
        keyboardShouldPersistTaps="handled"
        renderItem={({ item }) => (
          <Pressable
            onPress={() => onPick(item)}
            style={({ pressed }) => [styles.row, pressed && styles.rowPressed]}
            accessibilityRole="button"
            accessibilityLabel={`Search again for ${item}`}
          >
            <MaterialIcons
              name="history"
              size={16}
              color={fpColors.onSurfaceVariant}
              style={styles.rowIcon}
            />
            <Text style={styles.rowLabel} numberOfLines={1}>
              {item}
            </Text>
            <Pressable
              onPress={(e) => {
                e.stopPropagation();
                dispatch(removeRecent(item));
              }}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel={`Remove ${item} from recent searches`}
              testID={`recent-x-${item}`}
            >
              <MaterialIcons
                name="close"
                size={16}
                color={fpColors.onSurfaceVariant}
              />
            </Pressable>
          </Pressable>
        )}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  dropdown: {
    marginTop: 6,
    backgroundColor: fpColors.surfaceContainerLowest,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    overflow: 'hidden',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingTop: 10,
    paddingBottom: 4,
  },
  heading: {
    fontSize: 11,
    fontWeight: '700',
    color: fpColors.onSurfaceVariant,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  clearAll: {
    fontSize: 12,
    fontWeight: '600',
    color: fpColors.primary,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: fpColors.outlineVariant,
  },
  rowPressed: {
    backgroundColor: fpColors.surfaceContainerLow,
  },
  rowIcon: {
    marginRight: 8,
  },
  rowLabel: {
    flex: 1,
    fontSize: 14,
    color: fpColors.onSurface,
  },
});

export default RecentSearchesDropdown;
